import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Improvement {
  id: number
  improvable_type: string
  improvable_id: number
  improvable_name: string | null
  fields_changed_count: number
  status: string
  reviewed_by: string | null
  reviewed_at: string | null
  created_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  improvements: Improvement[]
  pagy: Pagination
  filters: {
    status: string
    improvable_type_eq?: string
  }
  stats: { pending: number; accepted: number; rejected: number; total: number }
}

function Index({ improvements, pagy, filters, stats }: Props) {
  const { t } = useTranslation('admin')
  const basePath = '/admin/collective_knowledge/improvements'

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = { status: filters.status || 'pending' }
    if (filters.improvable_type_eq) params['q[improvable_type_eq]'] = filters.improvable_type_eq

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleStatusChange = (value: string) => {
    router.get(basePath, { ...buildParams(), status: value }, { preserveState: true })
  }

  const handleTypeChange = (value: string) => {
    const params = buildParams()
    if (value === 'all') {
      delete params['q[improvable_type_eq]']
    } else {
      params['q[improvable_type_eq]'] = value
    }
    router.get(basePath, params, { preserveState: true })
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'warning' | 'success' | 'destructive'> = {
      pending: 'warning',
      accepted: 'success',
      rejected: 'destructive',
    }
    return (
      <Badge variant={variants[status] || 'secondary'}>
        {t(`collectiveKnowledge.improvements.status.${status}`)}
      </Badge>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('collectiveKnowledge.improvements.title')} ({pagy.count})
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Badge variant="warning" className="text-[10px] px-1 py-0">{stats.pending}</Badge>
                <span>/</span>
                <Badge variant="success" className="text-[10px] px-1 py-0">{stats.accepted}</Badge>
                <span>/</span>
                <Badge variant="destructive" className="text-[10px] px-1 py-0">{stats.rejected}</Badge>
              </div>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.status || 'pending'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder={t('collectiveKnowledge.improvements.filters.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('collectiveKnowledge.improvements.filters.all')}</SelectItem>
                  <SelectItem value="pending">{t('collectiveKnowledge.improvements.status.pending')}</SelectItem>
                  <SelectItem value="accepted">{t('collectiveKnowledge.improvements.status.accepted')}</SelectItem>
                  <SelectItem value="rejected">{t('collectiveKnowledge.improvements.status.rejected')}</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.improvable_type_eq || 'all'}
                onValueChange={handleTypeChange}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder={t('collectiveKnowledge.improvements.filters.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('collectiveKnowledge.improvements.filters.all')}</SelectItem>
                  <SelectItem value="Gotcha">{t('collectiveKnowledge.improvements.filters.gotcha')}</SelectItem>
                  <SelectItem value="Manual">{t('collectiveKnowledge.improvements.filters.manual')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.target')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.type')}
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.fields')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.status')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.created')}
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.improvements.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {improvements.length > 0 ? (
                      improvements.map((improvement) => (
                        <tr key={improvement.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <span className="text-sm font-medium text-foreground">
                              {improvement.improvable_name || `#${improvement.improvable_id}`}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant="secondary" className="text-xs">{improvement.improvable_type}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{improvement.fields_changed_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {getStatusBadge(improvement.status)}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(improvement.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`${basePath}/${improvement.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              {t('collectiveKnowledge.improvements.buttons.review')}
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('collectiveKnowledge.improvements.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.improvements.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.improvements.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
